import type { Texture } from "pixi.js";
import type { FeatureState } from "@slot/client-engine";
import type { GridLayer, Log, PresentationTaskRunner, SymbolView } from "@slot/presentation";
import { BASE_CELL_COUNT, GRID_CELL_COUNT, GRID_SIZE } from "../../gridSize";
import { BLANK, parseSymbolId, type CoinJackpotTier, type JackpotTier } from "../../rules/symbols";
import type { GameScene } from "../../scene/GameScene";
import type { MultiplierBadgesView } from "../../scene/MultiplierBadgesView";
import { CoinSymbolView } from "../../symbols/CoinSymbolView";
import { OUT_OF_PLAY } from "../../symbols/SymbolCatalog";
import { BOARD_IMPACT_TUNING, BOARD_TUNING, EDGE_FIRE_TUNING, FIRE_STREAK_TUNING } from "../../tuning/featureTuning";
import type { PotsPresenter } from "../pots/PotsPresenter";
import type { AwardFullBoard, BonusTrigger, Boosters, CollectBoard, CollectCoins, DropMultipliers, RevealMysteries } from "../wire";
import { FX_SCOPE } from "../../scene/fxScope";
import { MULTIPLIER_LAYOUT, frameBox } from "../../scene/layout";

/** The red glow under a trigger's cells at its brightest (alpha). */
const GLOW_ALPHA = 0.95;

/** The gold's glints on the bonus's moments (`ShinePresenter`). */
interface BonusShine {
  bonusTriggered(): void;
  jackpotWon(tier: JackpotTier): void;
  jackpotPaid(tier: JackpotTier): void;
}

/**
 * The bonus on the board: the trigger's cells glowing, the boosters' effects (GROW growing the grid, the ROYAL plaque),
 * the respin meter following the feature state (down as a respin starts, back up when it lands), the collects, MULTI's
 * badges, the mysteries' reveal, the full board's jackpot and the board paying into the total — every amount the
 * server's; this presenter only moves them. While the bonus plays the active lines step aside (decision 56: no line is
 * played in the bonus — the board unlit, the row frame and the LINES selector gone) and the jackpot plaques show the
 * bonus's values.
 */
export class HoldBonusPresenter {
  private readonly scene: GameScene;
  private readonly grid: GridLayer;
  private readonly badges: MultiplierBadgesView;
  private readonly badgeTexture: Texture | null;
  private readonly pots: PotsPresenter;
  private readonly format: (minor: string) => string;
  private readonly runner: PresentationTaskRunner;
  private readonly log: Log;
  private inBonus = false;
  private readonly lines: { setBonus(active: boolean): void };
  private readonly setJackpotBet: (betMinor: string | null) => void;
  private readonly shine: BonusShine | null;
  private readonly entry: { whenOver(): Promise<void> };

  public constructor(input: {
    scene: GameScene;
    grid: GridLayer;
    badges: MultiplierBadgesView;
    badgeTexture: Texture | null;
    pots: PotsPresenter;
    /** The money formatter without the currency symbol (the coins' and the total's digits). */
    format: (minor: string) => string;
    runner: PresentationTaskRunner;
    log: Log;
    /** The active lines (the lit rows, the row frame, the selector): they step aside while the bonus plays. */
    lines: { setBonus(active: boolean): void };
    /** The jackpot plaques' bet while a bonus plays (its values' bet), null back to a line's. */
    setJackpotBet: (betMinor: string | null) => void;
    /** The gold's glints on the bonus's moments. */
    shine?: BonusShine;
    /** The way into the bonus (the potion explosion as the HOLD & WIN card is dismissed): the boosters wait until it is over. */
    entry: { whenOver(): Promise<void> };
  }) {
    this.scene = input.scene;
    this.grid = input.grid;
    this.badges = input.badges;
    this.badgeTexture = input.badgeTexture;
    this.pots = input.pots;
    this.format = input.format;
    this.runner = input.runner;
    this.log = input.log;
    this.lines = input.lines;
    this.setJackpotBet = input.setJackpotBet;
    this.shine = input.shine ?? null;
    this.entry = input.entry;
  }

  /** The cells that start the bonus glow (the covered line, the full pot's gems) before the trigger panel. */
  public async presentTrigger(trigger: BonusTrigger): Promise<void> {
    if (trigger.cells.length === 0) return;
    for (const index of trigger.cells) this.scene.fx.burst({ at: this.scene.grid.cellCentre(index), tint: 0xffe070, count: 8, radius: 120, seconds: 0.6 });
    this.shine?.bonusTriggered();
    const glow = new Set(trigger.cells);
    await this.runner.tweenProgress(BOARD_TUNING.triggerHighlightSeconds, (k) => {
      this.scene.grid.setGlowing(glow, GLOW_ALPHA * Math.abs(Math.sin(Math.PI * 2 * k)));
    }, FX_SCOPE, { easing: "linear" });
    this.scene.grid.setGlowing(new Set(), 0);
  }

  /**
   * The bonus starts with its boosters: the meter shows (under the way into the bonus, the potion explosion, as it covers
   * the screen), then, once the explosion is over, each booster's pot lights in turn — LIFE's and GROW's erupting, LIFE's
   * fire streak falling onto the meter (its flames burn round it while its refill is to come), GROW's onto the grid's
   * centre (the grid grows, the ROYAL plaque replacing GRAND); MULTI's erupts only as it throws badges.
   */
  public async startBonus(boosters: Boosters): Promise<void> {
    this.inBonus = true;
    this.lines.setBonus(true);
    this.scene.meter.setShown(true);
    this.setJackpotBet(boosters.valueBetMinor);
    await this.entry.whenOver();
    await this.pots.activateBoosters(boosters, { life: () => this.lifeLands(), grow: () => this.growLands() });
  }

  /** LIFE's fire streak falls onto the meter, and its flames burn round it. */
  private async lifeLands(): Promise<void> {
    const meter = this.scene.meter.position;
    const to = { x: meter.x, y: meter.y };
    await this.scene.fx.fallStreak({ pot: "life", to, fallback: { from: this.pots.mouth("life"), to, lift: 300, seconds: BOARD_TUNING.refillFlightSeconds, texture: this.badgeTexture, size: 80, tint: 0x46ff70 } });
    this.scene.meter.setLifeFlames(true);
  }

  /**
   * GROW's fire streak falls onto the grid's centre (the composition's origin) and the board impact bursts there; as its
   * shockwave reaches the frame's edges (the artist's frame 8) the frame catches fire, and burns while the grid grows;
   * then it dies out.
   */
  private async growLands(): Promise<void> {
    const to = { x: 0, y: 0 };
    await this.scene.fx.fallStreak({ pot: "grow", to, fallback: { from: this.pots.mouth("grow"), to, lift: 260, seconds: BOARD_TUNING.refillFlightSeconds, texture: this.badgeTexture, size: 80, tint: 0x4aa0ff } });
    if (this.scene.fx.boardImpact.ready) {
      this.scene.fx.boardImpact.hit(to);
      await this.runner.sleep(BOARD_IMPACT_TUNING.growAfterSeconds, FX_SCOPE);
    }
    this.scene.edgeFire.set(true);
    this.scene.plaques.setRoyal(true);
    this.showGrowRows(true);
    try {
      await this.scene.setGrown(true, BOARD_TUNING.growSeconds);
    } finally {
      // It burns on a while over the grown grid, then dies out: the UI's time, never the round's.
      this.scene.edgeFire.goOutAfter(EDGE_FIRE_TUNING.afterGrowSeconds);
    }
  }

  /** The feature state each bonus event carries: the meter follows its count; at the exit the board returns to the base game. */
  public async onFeatureState(state: FeatureState): Promise<void> {
    if (state.featurePhase === "exit") {
      await this.endBonus();
      return;
    }
    this.inBonus = true;
    this.lines.setBonus(true);
    this.scene.meter.setShown(true);
    const count = Number(state.featureCountRemaining ?? "0");
    await this.scene.meter.setCount(count, BOARD_TUNING.meterPopSeconds, BOARD_TUNING.meterDrainSeconds);
  }

  /** LIFE's refill: its fire streak falls onto the meter, which lights back up, and LIFE's flames go out. */
  public async refillMeter(respins: number): Promise<void> {
    const meter = this.scene.meter.position;
    const to = { x: meter.x, y: meter.y };
    await this.scene.fx.fallStreak({
      pot: "life",
      to,
      fallback: { from: this.pots.mouth("life"), to, lift: 300, seconds: BOARD_TUNING.refillFlightSeconds, texture: this.badgeTexture, size: 80, tint: 0x46ff70 },
    });
    this.scene.fx.burst({ at: { x: meter.x, y: meter.y }, tint: 0x46ff70, count: 10, radius: 160, seconds: 0.5 });
    this.scene.meter.setLifeFlames(false);
    await this.scene.meter.setCount(respins, BOARD_TUNING.meterPopSeconds);
  }

  /**
   * A collect coin collects: its collector flares as it starts, each value flies into it, the collector pulsing and its
   * face counting up as each arrives; then it holds the sum.
   */
  public async collect(input: CollectCoins): Promise<void> {
    const view = this.grid.getSymbolViewAtIndex(input.collectorIndex);
    const collector = view instanceof CoinSymbolView ? view : null;
    const to = this.scene.grid.cellCentre(input.collectorIndex);
    collector?.activateCollector();
    let sum = 0n;
    const flights = input.sources.map(async (source, i) => {
      await this.runner.sleep(i * BOARD_TUNING.collectStaggerSeconds, FX_SCOPE);
      await this.scene.fx.flyAmount({ text: this.format(source.amountMinor), from: this.scene.grid.cellCentre(source.index), to, lift: 120, seconds: BOARD_TUNING.collectFlightSeconds, fontSize: 52 });
      sum += BigInt(source.amountMinor);
      collector?.pulseCollector();
      collector?.showValue(sum.toString());
    });
    await Promise.all(flights);
    this.scene.fx.burst({ at: to, tint: 0xffd040, count: 10, radius: 140, seconds: 0.5 });
    // The collect's id carries its value from now on (the next frame states the same).
    if (view) this.grid.pool.applySymbolIdToView(view, `COL:${input.valueMinor}`);
  }

  /**
   * MULTI's fire streaks fall onto their cells, each badge popping there as its streak lands in its ring of fire (a
   * raised one swells).
   */
  public async dropMultipliers(input: DropMultipliers): Promise<void> {
    const from = this.pots.mouth("multi");
    const targets = input.cells.map((hit) => this.scene.grid.cellCentre(hit.index));
    // Among those that land, some of MULTI's streaks carry nothing and fall on past the screen, each down a column no
    // multiplier lands in, the nearest the middle first (the user, 2026-10-12).
    const landing = new Set(input.cells.map((hit) => hit.index % GRID_SIZE.columns));
    const middle = (GRID_SIZE.columns - 1) / 2;
    const free = Array.from({ length: GRID_SIZE.columns }, (_, column) => column)
      .filter((column) => !landing.has(column))
      .sort((a, b) => Math.abs(a - middle) - Math.abs(b - middle));
    const passing = this.scene.fx.passStreaks({ pot: "multi", at: free.slice(0, FIRE_STREAK_TUNING.passers.count).map((column) => this.scene.grid.cellCentre(column).x) });
    for (const [i, hit] of input.cells.entries()) {
      if (i > 0) await this.runner.sleep(BOARD_TUNING.badgeStaggerSeconds, FX_SCOPE);
      const spot = this.badges.spot(hit.index);
      const corner = { x: spot.x + this.scene.grid.gridArea.x, y: spot.y + this.scene.grid.gridArea.y };
      // The streak carries the multiplier on its head, about the cell's size; caught on the cell's centre, the number
      // pops there, then scales down to its own size on its way to the cell's corner. As the streak lands, the cell's hit
      // bursts there in place of the streak's own ring, and the cell catches fire, burning while the multiplier waits.
      const target = targets[i]!;
      const cellHits = this.scene.fx.cellHits;
      const carried = await this.scene.fx.fallStreak({
        pot: "multi",
        to: target,
        label: { text: `x${hit.multiplier}`, to: corner, size: MULTIPLIER_LAYOUT.caughtFontSize / MULTIPLIER_LAYOUT.fontSize },
        onTouch: () => {
          cellHits.play(target);
          this.scene.cellFires.light(hit.index);
        },
        ring: !cellHits.ready,
        fallback: { from, to: corner, lift: 220, seconds: BOARD_TUNING.badgeFlightSeconds, texture: this.badgeTexture, size: BOARD_TUNING.badgeSize, tint: 0xff5a3a, scale: { from: 0.5, peak: 1.2, to: 1 } },
      });
      await this.badges.set(hit.index, hit.multiplier, BOARD_TUNING.badgePopSeconds, carried);
    }
    await passing;
  }

  /**
   * The mysteries are revealed, each in turn: a mystery jackpot's reel spins to its jackpot over the board (the designer's
   * mystery reel), a mystery coin (or a mystery jackpot before the reel's pieces are in) turns over into what it is.
   */
  public async revealMysteries(input: RevealMysteries): Promise<void> {
    for (const [i, cell] of input.cells.entries()) {
      if (i > 0) await this.runner.sleep(BOARD_TUNING.revealStaggerSeconds, FX_SCOPE);
      const view = this.grid.getSymbolViewAtIndex(cell.index);
      if (!view) {
        this.log(`[HoldBonus] reveal: no view on cell ${cell.index}`);
        continue;
      }
      const was = parseSymbolId(view.getSymbolId() ?? "");
      const revealed = parseSymbolId(cell.symbolId);
      if (was?.kind === "mystery" && was.jackpot && revealed?.kind === "jackpot" && this.scene.fx.mysteryReel.ready) {
        await this.spinMysteryJackpot(view, cell.index, cell.symbolId, revealed.tier);
        continue;
      }
      let swapped = false;
      await this.runner.tweenProgress(BOARD_TUNING.revealSeconds, (k) => {
        if (view.node.destroyed) return;
        if (!swapped && k >= 0.5) {
          swapped = true;
          this.grid.pool.applySymbolIdToView(view, cell.symbolId);
        }
        view.node.scale.set(Math.abs(Math.cos(Math.PI * k)), 1);
      }, FX_SCOPE, { easing: "linear" });
      if (!swapped) this.grid.pool.applySymbolIdToView(view, cell.symbolId);
      view.node.scale.set(1, 1);
      this.scene.fx.burst({ at: this.scene.grid.cellCentre(cell.index), tint: 0xd060ff, count: 8, radius: 110, seconds: 0.45 });
    }
  }

  /**
   * A mystery jackpot's reel, standing in for its cell, rises over the board's middle and spins to `tier`; as it is back,
   * the cell shows the jackpot coin.
   */
  private async spinMysteryJackpot(view: SymbolView, index: number, symbolId: string, tier: CoinJackpotTier): Promise<void> {
    const from = this.scene.grid.cellCentre(index);
    const box = frameBox(this.scene.grid.rows);
    view.node.visible = false;
    try {
      await this.scene.fx.mysteryReel.play({ from, to: { x: box.x + box.width / 2, y: box.y + box.height / 2 }, tier });
    } finally {
      this.grid.pool.applySymbolIdToView(view, symbolId);
      view.node.visible = true;
    }
    this.shine?.jackpotWon(tier);
    this.scene.fx.burst({ at: from, tint: 0xffd040, count: 10, radius: 130, seconds: 0.5 });
  }

  /** A full board: its jackpot's plaque celebrates over a burst across the grid. */
  public async awardFullBoard(input: AwardFullBoard): Promise<void> {
    for (let index = 0; index < GRID_SIZE.columns * this.scene.grid.rows; index++) {
      this.scene.fx.burst({ at: this.scene.grid.cellCentre(index), tint: 0xffe070, count: 6, radius: 100, seconds: 0.7 });
    }
    this.shine?.jackpotWon(input.jackpot);
    await this.scene.plaques.celebrate(input.jackpot, BOARD_TUNING.fullBoardSeconds);
  }

  /**
   * The board pays, cell by cell in the server's order (3 Coin Volcanoes', the user 2026-10-15): the total plaque opens in
   * the respin meter's place under the grid. A cell's multiplier goes into it, its fire dying down, and the coin's number
   * counts up to what the cell pays; then the amount rides a comet down to the plaque, the cell darkens as paid and the
   * plaque steps up to the running sum. A jackpot coin's amount (its plaque at the top glinting) and a collect coin's sum
   * go the same way. Last, the plaque lands on the server's total (the full board's jackpot in it, the cap applied). The
   * frame catches GROW's fire as the board starts paying and burns on until the bonus ends (the user, 2026-10-15).
   */
  public async collectBoard(input: CollectBoard): Promise<void> {
    const total = this.scene.bonusTotal;
    this.scene.edgeFire.set(true);
    this.scene.meter.setShown(false);
    await total.open(BOARD_TUNING.totalOpenSeconds);
    const target = { x: total.x, y: total.y };
    let running = 0n;
    for (const [i, cell] of input.cells.entries()) {
      if (i > 0) await this.runner.sleep(BOARD_TUNING.cellGapSeconds, FX_SCOPE);
      const view = this.grid.getSymbolViewAtIndex(cell.index);
      const coin = view instanceof CoinSymbolView ? view : null;
      const symbol = parseSymbolId(view?.getSymbolId() ?? "");
      if (this.badges.values().has(cell.index)) {
        this.scene.cellFires.douse(cell.index);
        await this.badges.merge(cell.index, BOARD_TUNING.mergeMoveSeconds, BOARD_TUNING.mergePopSeconds);
        const face = symbol?.kind === "coin" || symbol?.kind === "collect" ? symbol.valueMinor : null;
        if (coin && face !== null) await this.countFace(coin, BigInt(face), BigInt(cell.amountMinor));
      }
      if (symbol?.kind === "jackpot") this.shine?.jackpotPaid(symbol.tier);
      await this.scene.fx.flyToTotal({
        text: this.format(cell.amountMinor),
        from: this.scene.grid.cellCentre(cell.index),
        to: target,
        lift: BOARD_TUNING.cometLift,
        speed: BOARD_TUNING.cometSpeed,
        seconds: BOARD_TUNING.cellFlightSeconds,
        fontSize: 56,
      });
      coin?.setPaid(true);
      running += BigInt(cell.amountMinor);
      this.scene.fx.burst({ at: target, tint: 0xffd060, count: 8, radius: 90, seconds: 0.4 });
      void total.countTo(running, BOARD_TUNING.cellCountSeconds);
    }
    await this.runner.sleep(BOARD_TUNING.cellCountSeconds, FX_SCOPE);
    await total.countTo(BigInt(input.totalMinor), BOARD_TUNING.cellCountSeconds * 2);
    await this.runner.sleep(BOARD_TUNING.totalHoldSeconds, FX_SCOPE);
  }

  /** A coin's number counting up from `from` to `to` (what its cell pays, its multiplier in it). */
  private async countFace(coin: CoinSymbolView, from: bigint, to: bigint): Promise<void> {
    await this.runner.tweenProgress(BOARD_TUNING.multiplyCountSeconds, (k) => {
      coin.showValue((from + ((to - from) * BigInt(Math.round(k * 1000))) / 1000n).toString());
    }, FX_SCOPE, { easing: "quadOut" });
    coin.showValue(to.toString());
  }

  /** The bonus is over (after its finished panel): the board returns to the base game's. */
  public async endBonus(): Promise<void> {
    if (!this.inBonus) return;
    this.inBonus = false;
    this.scene.bonusTotal.close();
    this.scene.edgeFire.set(false);
    this.unpay();
    this.badges.clear();
    this.scene.cellFires.clear();
    this.scene.meter.setShown(false);
    this.scene.meter.setLifeFlames(false);
    void this.scene.meter.setCount(3, 0);
    this.lines.setBonus(false);
    this.setJackpotBet(null);
    this.pots.endBonus();
    this.scene.plaques.setRoyal(false);
    this.showGrowRows(false);
    await this.scene.setGrown(false, BOARD_TUNING.shrinkSeconds);
  }

  /** A recovery: the base game's board at once. */
  public reset(): void {
    this.inBonus = false;
    this.scene.bonusTotal.close();
    this.scene.edgeFire.set(false);
    this.unpay();
    this.badges.clear();
    this.scene.cellFires.clear();
    this.scene.meter.setShown(false);
    this.scene.meter.setLifeFlames(false);
    void this.scene.meter.setCount(3, 0);
    this.lines.setBonus(false);
    this.setJackpotBet(null);
    this.scene.plaques.setRoyal(false);
    this.scene.snapGrown(false);
    this.scene.grid.setGlowing(new Set(), 0);
  }

  /** The board's paid look gone: every cell as it shows. */
  private unpay(): void {
    for (let index = 0; index < GRID_CELL_COUNT; index++) {
      const view = this.grid.getSymbolViewAtIndex(index);
      if (view instanceof CoinSymbolView) view.setPaid(false);
    }
  }

  /** Every cell multiplier on the board (the dev hook's read). */
  public multipliers(): Map<number, number> {
    return this.badges.values();
  }

  /**
   * The GROW rows join the board or leave it. Joining, their blanks become the cells' panels before the frame grows over
   * them (the frame reveals the panels; the respins roll them). Leaving, every cell there is drawn as nothing before the
   * frame shrinks (the bonus has paid them, and no panel or coin may show past the frame's edge until the next board).
   */
  private showGrowRows(inPlay: boolean): void {
    for (let index = BASE_CELL_COUNT; index < GRID_CELL_COUNT; index++) {
      const shown = this.grid.getSymbolViewAtIndex(index)?.getSymbolId();
      if (inPlay && shown === OUT_OF_PLAY) this.grid.renderCell(index, BLANK);
      if (!inPlay && shown !== OUT_OF_PLAY) this.grid.renderCell(index, OUT_OF_PLAY);
    }
  }
}
